// ============================================
// Agent Attack Chain Visualizer
// ============================================

import {
  SCENARIOS,
  getActiveDefenses,
  getEarliestBlock,
  getTerminalStep,
  type DefenseState,
  type Scenario,
} from '../../lib/labs/agent-attack-chain';

// ============================================
// UI Logic
// ============================================
const root = document.querySelector('[data-agent-attack-chain]') as HTMLElement | null;

if (root) {
  // DOM Elements
  const chainPlay = root.querySelector('#chain-play') as HTMLButtonElement;
  const chainStep = root.querySelector('#chain-step') as HTMLButtonElement;
  const chainReset = root.querySelector('#chain-reset') as HTMLButtonElement;
  const chainProgress = root.querySelector('#chain-progress') as HTMLElement;
  const chainProgressText = root.querySelector('#chain-progress-text') as HTMLElement;
  const chainStageTitle = root.querySelector('#chain-stage-title') as HTMLElement;
  const chainStageDesc = root.querySelector('#chain-stage-desc') as HTMLElement;
  const agentLogCode = root.querySelector('#agent-log code') as HTMLElement;
  const agentTools = root.querySelector('#agent-tools') as HTMLElement;
  const agentContext = root.querySelector('#agent-context') as HTMLElement;
  const agentPermissions = root.querySelector('#agent-permissions') as HTMLElement;

  // Scenario elements
  const scenarioLabel = root.querySelector('#scenario-label') as HTMLElement;
  const scenarioDesc = root.querySelector('#scenario-desc') as HTMLElement;
  const scenarioBtns = root.querySelectorAll('.scenario-btn');

  // Defense elements
  const defenseToolSandbox = root.querySelector(
    '#defense-tool-sandbox'
  ) as HTMLInputElement;
  const defenseOutputFilter = root.querySelector(
    '#defense-output-filter'
  ) as HTMLInputElement;
  const defensePermissionBoundary = root.querySelector(
    '#defense-permission-boundary'
  ) as HTMLInputElement;
  const defenseIoSeparation = root.querySelector(
    '#defense-io-separation'
  ) as HTMLInputElement;
  const defenseStatus = root.querySelector('#defense-status') as HTMLElement;
  const defenseImpact = root.querySelector('#defense-impact') as HTMLElement;
  const defenseImpactTitle = root.querySelector('#defense-impact-title') as HTMLElement;
  const defenseImpactDesc = root.querySelector('#defense-impact-desc') as HTMLElement;

  // Defense stage labels
  const defenseStageToolSandbox = root.querySelector(
    '#defense-stage-tool-sandbox'
  ) as HTMLElement;
  const defenseStageOutputFilter = root.querySelector(
    '#defense-stage-output-filter'
  ) as HTMLElement;
  const defenseStagePermissionBoundary = root.querySelector(
    '#defense-stage-permission-boundary'
  ) as HTMLElement;
  const defenseStageIoSeparation = root.querySelector(
    '#defense-stage-io-separation'
  ) as HTMLElement;

  // State
  let currentScenario: Scenario = SCENARIOS[0];
  let chainStepIndex = -1;
  let chainPlaying = false;
  let chainInterval: ReturnType<typeof setInterval> | null = null;

  let defenses: DefenseState = {
    toolSandbox: false,
    outputFilter: false,
    permissionBoundary: false,
    ioSeparation: false,
  };

  function updateDefenseStageLabels() {
    const effects = currentScenario.defenseEffects;
    defenseStageToolSandbox.textContent = `Blocks at: Stage ${effects.toolSandbox.blocksAt + 1}`;
    defenseStageOutputFilter.textContent = `Blocks at: Stage ${effects.outputFilter.blocksAt + 1}`;
    defenseStagePermissionBoundary.textContent = `Blocks at: Stage ${effects.permissionBoundary.blocksAt + 1}`;
    defenseStageIoSeparation.textContent = `Blocks at: Stage ${effects.ioSeparation.blocksAt + 1}`;
  }

  function updateStepLabels() {
    const stages = currentScenario.stages;
    for (let i = 0; i < stages.length; i++) {
      const titleEl = root.querySelector(`[data-step-title="${i}"]`) as HTMLElement;
      const descEl = root.querySelector(`[data-step-desc="${i}"]`) as HTMLElement;
      if (titleEl) {
        // Extract short title from full title (remove "Stage N: " prefix)
        const shortTitle = stages[i].title.replace(/^Stage \d+:\s*/, '');
        titleEl.textContent = shortTitle;
      }
      if (descEl) {
        // Use a short version of the desc for the chain node
        const words = stages[i].desc.split(' ').slice(0, 6).join(' ');
        descEl.textContent = words + '...';
      }
    }
  }

  function updateDefenseStatus() {
    const active = getActiveDefenses(defenses);
    if (active.length === 0) {
      defenseStatus.textContent = 'All defenses off';
      defenseStatus.classList.remove('border-emerald-500/50', 'text-emerald-500');
      defenseStatus.classList.add('border-border/70', 'text-muted');
    } else {
      defenseStatus.textContent = `${active.length} defense${active.length > 1 ? 's' : ''} active`;
      defenseStatus.classList.remove('border-border/70', 'text-muted');
      defenseStatus.classList.add('border-emerald-500/50', 'text-emerald-500');
    }

    const effect = getEarliestBlock(currentScenario, defenses);
    if (effect && chainStepIndex >= effect.blocksAt) {
      defenseImpact.classList.remove('hidden');
      defenseImpactTitle.textContent = effect.title;
      defenseImpactDesc.textContent =
        effect.desc + (effect.bypassNote ? ` (${effect.bypassNote})` : '');
    } else if (effect) {
      defenseImpact.classList.remove('hidden');
      defenseImpactTitle.textContent = 'Defense Active';
      defenseImpactDesc.textContent = `Exploit will be blocked at stage ${effect.blocksAt + 1}. Play the chain to see.`;
    } else {
      defenseImpact.classList.add('hidden');
    }
  }

  function updateChainVisualization() {
    const steps = root.querySelectorAll('.chain-step');
    const arrows = root.querySelectorAll('.chain-arrow');
    const stages = currentScenario.stages;
    const effect = getEarliestBlock(currentScenario, defenses);
    const blockStage = effect ? effect.blocksAt : -1;
    const terminalStep = getTerminalStep(currentScenario, defenses);

    if (chainStepIndex > terminalStep) {
      chainStepIndex = terminalStep;
    }

    steps.forEach((step, i) => {
      const node = step.querySelector('.chain-node') as HTMLElement;
      if (!node) return;
      node.classList.remove(
        'pending',
        'active',
        'complete',
        'danger',
        'blocked',
        'defended'
      );
      node.style.position = 'relative';

      if (blockStage >= 0 && i >= blockStage) {
        if (i === blockStage && chainStepIndex >= blockStage) {
          node.classList.add('defended');
        } else if (i > blockStage) {
          node.classList.add('blocked');
        } else {
          node.classList.add('pending');
        }
      } else if (i < chainStepIndex) {
        node.classList.add('complete');
      } else if (i === chainStepIndex) {
        node.classList.add(chainStepIndex >= 3 ? 'danger' : 'active');
      } else {
        node.classList.add('pending');
      }
    });

    arrows.forEach((arrow, i) => {
      arrow.classList.remove('active', 'complete', 'danger', 'blocked');

      if (blockStage >= 0 && i >= blockStage) {
        arrow.classList.add('blocked');
      } else if (i < chainStepIndex) {
        arrow.classList.add('complete');
      } else if (i === chainStepIndex - 1 && chainStepIndex > 0) {
        arrow.classList.add(chainStepIndex >= 4 ? 'danger' : 'active');
      }
    });

    // Update progress bar
    const progress =
      chainStepIndex >= 0 ? ((chainStepIndex + 1) / stages.length) * 100 : 0;
    chainProgress.style.width = `${progress}%`;
    chainProgressText.textContent =
      chainStepIndex >= 0
        ? `Stage ${chainStepIndex + 1} of ${stages.length}`
        : 'Ready to start';

    if (chainStepIndex >= 0 && chainStepIndex < stages.length) {
      const stage = stages[chainStepIndex];

      if (effect && chainStepIndex >= effect.blocksAt) {
        chainStageTitle.textContent = effect.title;
        chainStageDesc.textContent = effect.desc;
        agentLogCode.innerHTML = `<span class="code-comment">// DEFENSE ACTIVATED</span>
<span class="code-comment">// ${effect.title}</span>

<span class="code-keyword">blocked</span>: <span class="code-str">"${effect.desc.substring(0, 80)}..."</span>

${effect.bypassNote ? `<span class="code-comment">// Bypass note: ${effect.bypassNote}</span>` : '<span class="code-comment">// No known bypass for this defense configuration.</span>'}

<span class="code-comment">// Attack chain terminated at stage ${effect.blocksAt + 1}</span>`;
        agentTools.textContent = 'Execution halted by active defense';
        agentContext.textContent = `Attack blocked at stage ${effect.blocksAt + 1}`;
        agentPermissions.textContent = 'No unauthorized permission change';
      } else {
        chainStageTitle.textContent = stage.title;
        chainStageDesc.textContent = stage.desc;
        agentLogCode.innerHTML = stage.agentLog;
        // Update agent state panel
        agentTools.textContent = stage.agentState.tools;
        agentContext.textContent = stage.agentState.context;
        agentPermissions.textContent = stage.agentState.permissions;
      }

      // Highlight agent state rows based on step
      const stateRows = root.querySelectorAll('.agent-state-row');
      stateRows.forEach((row) => {
        const rowName = row.getAttribute('data-row');
        row.classList.remove('corrupted', 'highlight');
        if (
          !(effect && chainStepIndex >= effect.blocksAt) &&
          chainStepIndex >= 3 &&
          rowName === 'permissions'
        ) {
          row.classList.add('corrupted');
        } else if (chainStepIndex >= 1 && rowName === 'context') {
          row.classList.add('highlight');
        }
      });
    } else {
      chainStageTitle.textContent = 'Waiting to start';
      chainStageDesc.textContent =
        'Click "Play Chain" to step through the agent attack chain, or use "Step" to advance one stage at a time.';
      agentLogCode.innerHTML =
        '<span class="text-slate-400">// Agent action log will appear here during the attack chain</span>';
      agentTools.textContent = '--';
      agentContext.textContent = '--';
      agentPermissions.textContent = '--';
      const stateRows = root.querySelectorAll('.agent-state-row');
      stateRows.forEach((row) => row.classList.remove('corrupted', 'highlight'));
    }

    updateDefenseStatus();
  }

  function playChainStepFn() {
    const stages = currentScenario.stages;
    if (
      chainStepIndex >= getTerminalStep(currentScenario, defenses) ||
      chainStepIndex >= stages.length - 1
    ) {
      stopChain();
      return;
    }
    chainStepIndex++;
    updateChainVisualization();
  }

  function stopChain() {
    chainPlaying = false;
    if (chainInterval) {
      clearInterval(chainInterval);
      chainInterval = null;
    }
    chainPlay.textContent =
      chainStepIndex >= getTerminalStep(currentScenario, defenses)
        ? 'Replay'
        : 'Play Chain';
  }

  function resetChain() {
    stopChain();
    chainStepIndex = -1;
    updateChainVisualization();
    chainPlay.textContent = 'Play Chain';
  }

  function startChain() {
    chainPlaying = true;
    chainPlay.textContent = 'Pause';
    chainInterval = setInterval(playChainStepFn, 2500);
    playChainStepFn();
  }

  function setScenario(scenarioId: string) {
    const scenario = SCENARIOS.find((s) => s.id === scenarioId);
    if (!scenario) return;

    currentScenario = scenario;

    // Update scenario selector UI
    scenarioBtns.forEach((btn) => {
      const btnScenario = btn.getAttribute('data-scenario');
      if (btnScenario === scenarioId) {
        btn.classList.add('active');
        btn.classList.remove(
          'border-border/70',
          'text-muted',
          'hover:border-accent',
          'hover:text-accent'
        );
        btn.classList.add('border-accent', 'bg-accent/10', 'text-accent');
      } else {
        btn.classList.remove('active', 'border-accent', 'bg-accent/10', 'text-accent');
        btn.classList.add(
          'border-border/70',
          'text-muted',
          'hover:border-accent',
          'hover:text-accent'
        );
      }
    });

    // Update description
    scenarioLabel.textContent = scenario.title;
    scenarioDesc.textContent = scenario.description;

    // Update step labels and defense labels
    updateStepLabels();
    updateDefenseStageLabels();

    // Reset chain
    resetChain();
  }

  function cycleScenario() {
    const currentIndex = SCENARIOS.findIndex((s) => s.id === currentScenario.id);
    const nextIndex = (currentIndex + 1) % SCENARIOS.length;
    setScenario(SCENARIOS[nextIndex].id);
  }

  // Event Listeners - Scenario buttons
  scenarioBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const scenarioId = btn.getAttribute('data-scenario');
      if (scenarioId) setScenario(scenarioId);
    });
  });

  // Event Listeners - Play/Step/Reset
  chainPlay?.addEventListener('click', () => {
    if (chainStepIndex >= getTerminalStep(currentScenario, defenses)) {
      resetChain();
      setTimeout(startChain, 300);
    } else if (chainPlaying) {
      stopChain();
    } else {
      startChain();
    }
  });

  chainStep?.addEventListener('click', () => {
    stopChain();
    if (chainStepIndex >= getTerminalStep(currentScenario, defenses)) {
      resetChain();
    } else {
      chainStepIndex++;
      updateChainVisualization();
    }
  });

  chainReset?.addEventListener('click', resetChain);

  // Defense listeners
  defenseToolSandbox?.addEventListener('change', () => {
    defenses.toolSandbox = defenseToolSandbox.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defenseOutputFilter?.addEventListener('change', () => {
    defenses.outputFilter = defenseOutputFilter.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defensePermissionBoundary?.addEventListener('change', () => {
    defenses.permissionBoundary = defensePermissionBoundary.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  defenseIoSeparation?.addEventListener('change', () => {
    defenses.ioSeparation = defenseIoSeparation.checked;
    updateDefenseStatus();
    updateChainVisualization();
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)
      return;

    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      chainPlay?.click();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      chainStep?.click();
    } else if (e.key === 'r' || e.key === 'R') {
      e.preventDefault();
      chainReset?.click();
    } else if (e.key === '1') {
      e.preventDefault();
      defenseToolSandbox.checked = !defenseToolSandbox.checked;
      defenseToolSandbox.dispatchEvent(new Event('change'));
    } else if (e.key === '2') {
      e.preventDefault();
      defenseOutputFilter.checked = !defenseOutputFilter.checked;
      defenseOutputFilter.dispatchEvent(new Event('change'));
    } else if (e.key === '3') {
      e.preventDefault();
      defensePermissionBoundary.checked = !defensePermissionBoundary.checked;
      defensePermissionBoundary.dispatchEvent(new Event('change'));
    } else if (e.key === '4') {
      e.preventDefault();
      defenseIoSeparation.checked = !defenseIoSeparation.checked;
      defenseIoSeparation.dispatchEvent(new Event('change'));
    } else if (e.key === 's' || e.key === 'S') {
      e.preventDefault();
      cycleScenario();
    }
  });

  // Initialize
  updateStepLabels();
  updateDefenseStageLabels();
  updateChainVisualization();
}
